import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import CommentThread from '../../components/comments/CommentThread';
import { setObservationCommentsOpen } from '../../store/ui/uiActions';
import { getModel } from '../../store/api/selectors/model';
import { getTimeline } from '../../store/timeline/timelineSelectors';
import { ReactComponent as IconClose } from '../../svgs/icon-close.svg';

const Dock = styled.aside`
  position: fixed;
  bottom: 0;
  right: 0;
  width: 24rem;
  max-width: 100vw;
  height: var(--app-bottom-height);
  background-color: var(--color-dark);
  border-radius: 0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  z-index: 1100;
  transform: translateY(${({ open }) => (open ? '0' : '100%')});
  transition: transform 0.3s ease;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 14px;
  background-color: var(--color-darkest);
  gap: 8px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const HeaderLabel = styled.div`
  font-weight: bold;
  font-size: 0.9rem;
  color: var(--color-white);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderDescription = styled.div`
  font-size: 0.75rem;
  color: var(--color-gray);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  & svg {
    width: 12px;
    height: 12px;
  }
  & svg path {
    fill: var(--color-white);
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 8px;
`;

const CommentThreadDock = () => {
  const dispatch = useDispatch();
  const functionId = useSelector((state) => state.ui.uiObservationCommentsOpen);
  const model = useSelector(getModel);
  const timeline = useSelector(getTimeline);
  const dockRef = useRef(null);

  const open = Boolean(functionId);
  const meta = functionId ? model[functionId] : null;
  const timelineEntry = functionId ? timeline.find((t) => t.functionId === functionId) : null;
  const fault = timelineEntry ? timelineEntry.state : null;

  const handleClose = () => dispatch(setObservationCommentsOpen(null));

  useEffect(() => {
    if (!open) return undefined;
    const handleDocClick = (e) => {
      if (dockRef.current && dockRef.current.contains(e.target)) return;
      // Only the sidebar toggle button is exempt — opening the messages sidebar
      // shouldn't close the comment box. All other outside clicks close it.
      const toggle = e.target.closest('[data-comments-toggle="true"]');
      if (toggle) return;
      dispatch(setObservationCommentsOpen(null));
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [open, dispatch]);

  return (
    <Dock open={open} aria-hidden={!open} ref={dockRef}>
      <Header>
        <HeaderText>
          <HeaderLabel>{meta?.label || functionId || 'Observation'}</HeaderLabel>
          {meta?.description && <HeaderDescription>{meta.description}</HeaderDescription>}
        </HeaderText>
        <CloseButton onClick={handleClose} aria-label="close comments">
          <IconClose />
        </CloseButton>
      </Header>
      <Body>
        {functionId && (
          <CommentThread
            key={functionId}
            functionId={functionId}
            observationLabel={meta?.label || ''}
            observationDescription={meta?.description || ''}
            observationFault={fault}
          />
        )}
      </Body>
    </Dock>
  );
};

export default CommentThreadDock;
